import React from "react";
import MobileModuleHeader from "../../components/MobileModuleHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import {
  resetUser,
  selectHasUserPropertySetting,
  selectUser,
  selectUserRole,
} from "../../slices/user";
import api from "../../api";
import { AccessKeyCreateResponse } from "../../api/models/access-key-model";
import InvitationCardGuard from "../../components/InvitationCardGuard";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Context } from "../../context/ContextProvider/ContextProvider";
import { showError } from "../../utils/helpers";
import UserQrCode from "../../components/UserQrCode";
import UserModal from "../../components/Modals/UserModal";
import Logo from "../../components/Logo";
import { resetToken } from "../../slices/token";
import IconifyIcon from "../../components/IconifyIcon";

const MobAccessKeys = () => {
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const labels = useAppSelector(selectLabels);
  const navigate = useNavigate();
  const isGuard = userRole === "guard";
  const dispatch = useAppDispatch();
  const hasSecUserPerms = useAppSelector(
    selectHasUserPropertySetting("sec_user_perms")
  );
  const isSecondaryHostRestricted =
    userRole === "secondary_host" && !hasSecUserPerms;

  const propertyId = user?.property?.id;
  const unitId = user?.unit?.id;

  const { selectedAccessPointGuard } = React.useContext(Context);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [accessKeys, setAccessKeys] = React.useState<
    AccessKeyCreateResponse[] | null
  >(null);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalInvitations, setTotalInvitations] = React.useState<number>(0);
  const [selectedAccessPoint, setSelectedAccessPoint] =
    React.useState<string>("0");
  const [selectedState, setSelectedState] = React.useState<
    "" | "access" | "exit" | "try" | "deleted" | "unused"
  >("");
  const [showUserDetails, setShowUserDetails] = React.useState<boolean>(false);

  const debouncedSearchFilter = useDebounce(searchFilter, 300);

  const fixedPage = React.useMemo(() => {
    return page + 1;
  }, [page]);

  const accessPointsArray = user?.access_points
    ? user.access_points.map((item) => {
        return {
          label: item?.name,
          value: `${item.id}`,
        };
      })
    : [];

  accessPointsArray.unshift({
    label: labels.all_access_points,
    value: "0",
  });

  const getAccessKeys = React.useCallback(
    async (debouncedText: string) => {
      if (propertyId) {
        let currentState;
        let currentAccessPoint;
        if (selectedState === "") {
          currentState = undefined;
        } else {
          currentState = selectedState;
        }
        if (!isGuard) {
          if (selectedAccessPoint === "0") {
            currentAccessPoint = undefined;
          } else {
            currentAccessPoint = Number(selectedAccessPoint);
          }
        } else {
          if (!selectedAccessPointGuard) {
            currentAccessPoint = undefined;
          } else {
            currentAccessPoint = Number(selectedAccessPointGuard);
          }
        }
        try {
          setIsLoading(true);
          const response = await api.accessKeysByPropertyRequest(
            Number(propertyId),
            fixedPage,
            rowsPerPage,
            undefined,
            currentState,
            currentAccessPoint,
            undefined,
            undefined,
            undefined,
            debouncedText,
            userRole === "host" ? (unitId ?? -1) : undefined,
            userRole === "secondary_host" ? (user?.id ?? -1) : undefined,
          );
          if (response) {
            setAccessKeys(response?.data);
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error
          );
        } finally {
          setIsLoading(false);
        }
      }
    },
    [
      dispatch,
      fixedPage,
      isGuard,
      labels.generic_error,
      propertyId,
      rowsPerPage,
      selectedAccessPoint,
      selectedAccessPointGuard,
      selectedState,
      unitId,
      userRole,
      user?.id,
    ]
  );

  React.useEffect(() => {
    getAccessKeys(debouncedSearchFilter);
  }, [debouncedSearchFilter, getAccessKeys]);

  const onLogout = () => {
    dispatch(resetToken());
    dispatch(resetUser());
    navigate("/");
  };

  if (isSecondaryHostRestricted) {
    return (
      <div className="w-100 flex" style={{ position: "relative" }}>
        <>
          <UserModal
            showUserDetails={showUserDetails}
            setShowUserDetails={setShowUserDetails}
            onLogout={onLogout}
          />
          <div className="mobile-header">
            <Logo
              type={"passTrackLogo"}
              alt="passtrack"
              className="mobile-logo"
            />
            <div className="flex align-items-center gap-6">
              <div
                className="user-init-container"
                onClick={() => setShowUserDetails(true)}
              >
                <IconifyIcon
                  icon={"fa-solid:user-circle"}
                  fontSize={28}
                  className="primary-color"
                />
              </div>
            </div>
          </div>
          <div className="mobile-content-container">
            <div
              className="mobile-module-body"
              style={{ position: "relative" }}
            >
              <div className="w-100 h-100 flex justify-content-center align-items-center">
                <UserQrCode />
              </div>
            </div>
          </div>
        </>
      </div>
    );
  }

  return (
    <div className="mobile-module-body" style={{ position: "relative" }}>
      <div
        style={{
          zIndex: 99,
          position: "relative",
        }}
      >
        <MobileModuleHeader
          title={labels.access_keys}
          titleIcon={"fa-solid:envelope-open-text"}
          searchValue={searchFilter}
          setSearchValue={setSearchFilter}
          hasSearch={true}
          hasFilters={false}
          isDowloadable={false}
          hasNewButton={userRole === "guard" ? false : true}
          newButtonAction={() => navigate("/new-access-key")}
        />
      </div>
      <div
        className={"mobile-module-body-no-filters-content"}
        style={{ zIndex: 10, position: "relative" }}
      >
        {isLoading ? (
          <div className="w-100 h-100 flex justify-content-center align-items-center">
            <CircularProgress />
          </div>
        ) : (
          <>
            {accessKeys &&
              accessKeys.length > 0 &&
              accessKeys.map((item, index) => {
                const varifiableId = item?.verifiable_ids[0]?.documentId;
                return (
                  <InvitationCardGuard
                    style={{
                      marginBottom: accessKeys.length - 1 === index ? 0 : 8,
                    }}
                    key={`${item?.id}-${index}`}
                    selectedAccessKey={item}
                    onPress={() =>
                      navigate(`/access-key-details/${varifiableId}`)
                    }
                  />
                );
              })}
          </>
        )}
      </div>
    </div>
  );
};

export default MobAccessKeys;
